import { useState } from 'react';

interface ConfirmButtonProps {
  /** Beschriftung im Initial-Zustand */
  label: string;
  /** Wird aufgerufen wenn der User die Aktion bestaetigt hat */
  onConfirm: (reason: string | null) => void;
  /** Wenn true: in der Bestaetigungsphase erscheint ein Input fuer einen Grund */
  withReason?: boolean;
  reasonPlaceholder?: string;
  /** Mit true muss ein nicht-leerer Grund eingegeben werden */
  reasonRequired?: boolean;
  /** Beschriftung des aktivierten Bestaetigen-Buttons (default: "Wirklich?" oder "Bestaetigen") */
  confirmLabel?: string;
  disabled?: boolean;
  /** Visuelle Variante des Initial-Buttons */
  variant?: 'danger' | 'neutral';
  /** Optionale Klassen am Initial-Button */
  className?: string;
  /** Optional: Fehlermeldung die nach dem Versuch angezeigt wird */
  error?: string | null;
}

export function ConfirmButton({
  label,
  onConfirm,
  withReason = false,
  reasonPlaceholder,
  reasonRequired = false,
  confirmLabel,
  disabled = false,
  variant = 'danger',
  className = '',
  error = null,
}: ConfirmButtonProps) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');

  const cancel = () => {
    setConfirming(false);
    setReason('');
  };

  const submit = () => {
    if (reasonRequired && !reason.trim()) return;
    onConfirm(reason.trim() || null);
    setConfirming(false);
    setReason('');
  };

  const initialClasses =
    variant === 'danger'
      ? 'border-rose-900 bg-rose-950/40 text-rose-200 hover:bg-rose-900/60'
      : 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700';

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={disabled}
        className={`rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-60 ${initialClasses} ${className}`}
      >
        {label}
      </button>
    );
  }

  if (withReason) {
    return (
      <div className={`space-y-2 rounded-lg border border-rose-900 bg-rose-950/30 p-2 ${className}`}>
        <input
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            reasonPlaceholder ??
            (reasonRequired ? 'Grund (Pflicht)' : 'Grund (optional)')
          }
          className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={submit}
            disabled={disabled || (reasonRequired && !reason.trim())}
            className="flex-1 rounded bg-rose-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-600 disabled:opacity-60"
          >
            {confirmLabel ?? 'Bestätigen'}
          </button>
          <button
            type="button"
            onClick={cancel}
            className="rounded border border-slate-700 px-3 py-1.5 text-xs"
          >
            Abbrechen
          </button>
        </div>
        {error && <p className="text-xs text-rose-400">{error}</p>}
      </div>
    );
  }

  return (
    <div className={`flex gap-2 ${className}`}>
      <button
        type="button"
        onClick={submit}
        disabled={disabled}
        className="flex-1 rounded-lg bg-rose-700 px-3 py-2 text-sm font-medium text-white hover:bg-rose-600 disabled:opacity-60"
      >
        {confirmLabel ?? 'Wirklich?'}
      </button>
      <button
        type="button"
        onClick={cancel}
        className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm"
      >
        Abbrechen
      </button>
      {error && <p className="text-xs text-rose-400">{error}</p>}
    </div>
  );
}
